import React, { forwardRef, ReactNode } from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import * as PopoverPrimitive from "@radix-ui/react-popover";

type PopoverProps = {
  trigger: ReactNode; // the element that will trigger the dropdown, e.g. a button.
  // the component must spread props and forward refs
  children: ReactNode; // the content of the dropdown menu, e.g. a list of items.

  // Trigger Props
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Other Props
  hideArrow?: boolean; // if true, the arrow will not be shown
} & React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>;

/**
 * @param trigger - The element that will trigger the dropdown, e.g. a button.
 * @param children - The content of the dropdown menu, e.g. a list of items.
 * @param open - Controls the open state of the popover.
 * @param onOpenChange - Callback function to handle open state changes.
 * @param side - The side of the popover relative to the trigger (top, right, bottom, left).
 * @param align - The alignment of the popover relative to the trigger (start, center, end).
 * @param hideArrow - If true, the arrow will not be shown.
 */
const Popover = forwardRef<React.ElementRef<typeof PopoverPrimitive.Content>, PopoverProps>(
  ({ trigger, children, open, onOpenChange, hideArrow = false, ...props }, ref) => (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <Content ref={ref} arrowPadding={4} sideOffset={hideArrow ? 4 : undefined} {...props}>
        {!hideArrow && <Arrow />}
        {children}
      </Content>
    </PopoverPrimitive.Root>
  ),
);

const animationIn = keyframes({
  from: {
    opacity: "var(--enter-opacity, 1)",
    transform: `translate3d(var(--enter-translate-x, 0), var(--enter-translate-y, 0), 0)
                scale3d(var(--enter-scale, 1), var(--enter-scale, 1), var(--enter-scale, 1))`,
  },
});

const animationOut = keyframes({
  to: {
    opacity: "var(--exit-opacity, 1)",
    transform: `translate3d(var(--exit-translate-x, 0), var(--exit-translate-y, 0), 0)
                scale3d(var(--exit-scale, 1), var(--exit-scale, 1), var(--exit-scale, 1))`,
  },
});

const Content = styled(PopoverPrimitive.Content)(({ theme }) => ({
  zIndex: 1000,
  backgroundColor: theme.colors.sysBackgroundDefault,
  borderRadius: "4px",
  padding: "8px",
  boxShadow:
    "rgba(0, 0, 0, 0.08) 0px 6px 16px 0px, rgba(0, 0, 0, 0.12) 0px 3px 6px -4px, rgba(0, 0, 0, 0.05) 0px 9px 28px 8px",

  transformOrigin: "var(--radix-popover-content-transform-origin)",
  [`&[data-state="open"]`]: {
    animation: `${animationIn} 150ms ease`,
    "--enter-opacity": 0,
    "--exit-opacity": 1,
    "--enter-scale": 0.9,
    "--exit-scale": 1,
    [`&[data-side="top"]`]: {
      "--enter-translate-y": "8px",
    },
    [`&[data-side="bottom"]`]: {
      "--enter-translate-y": "-8px",
    },
    [`&[data-side="left"]`]: {
      "--enter-translate-x": "8px",
    },
    [`&[data-side="right"]`]: {
      "--enter-translate-x": "-8px",
    },
  },
  [`&[data-state="closed"]`]: {
    animation: `${animationOut} 150ms ease`,
    "--enter-opacity": 1,
    "--exit-opacity": 0,
    "--enter-scale": 1,
    "--exit-scale": 0.9,
    [`&[data-side="top"]`]: {
      "--exit-translate-y": "8px",
    },
    [`&[data-side="bottom"]`]: {
      "--exit-translate-y": "-8px",
    },
    [`&[data-side="left"]`]: {
      "--exit-translate-x": "8px",
    },
    [`&[data-side="right"]`]: {
      "--exit-translate-x": "-8px",
    },
  },
}));

const Arrow = styled(PopoverPrimitive.Arrow)(({ theme }) => ({
  fill: theme.colors.sysBackgroundDefault,
}));

Popover.displayName = PopoverPrimitive.Root.displayName;

export type PopoverAlign = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>["align"];

export default Popover;
